import type {
  AlertState,
  AlertView,
  Attestation,
  AuditResponse,
  BrandingSettings,
  BrandingView,
  Capability,
  ControlSummary,
  CsvRowError,
  DeliveryUpdate,
  DeliveryView,
  DemoFixture,
  DriftReport,
  EstateFindingsResponse,
  Finding,
  Framework,
  ImportEntry,
  ImportResult,
  MeView,
  NewUser,
  OnboardingView,
  PortfolioTenant,
  ProductSummary,
  RiskAcceptance,
  ProgressReport,
  Role,
  Scan,
  ScanResult,
  SchedulePlan,
  SchedulerConfig,
  SessionView,
  Tenant,
  TenantAlertCount,
  TenantSchedule,
  UserState,
  UserView,
  WaitlistJoin,
  WaitlistSignup,
} from './dto.ts';
import type {
  AccountDetail,
  AccountPackage,
  Allowance,
  NewOwner,
  PackageView,
  PlatformAccount,
  PlatformScores,
  PlatformTenant,
  RepricedAccountView,
  SubscriptionView,
} from './dto.ts';
import type {
  NewTicket,
  NewTicketMessage,
  PlatformTicketDetail,
  PlatformTicketSummary,
  TicketDetail,
  TicketStatus,
  TicketSummary,
} from './dto.ts';
import { openDocument, type OpenReportOptions } from './openDocument.ts';
import { accountSuspended, isAccountSuspended } from './accountSuspension.ts';

export type { OpenReportOptions } from './openDocument.ts';

export type {
  AttachmentMediaType,
  AttachmentView,
  NewAttachment,
  NewTicket,
  NewTicketMessage,
  PlatformTicketDetail,
  PlatformTicketSummary,
  TicketDetail,
  TicketMessage,
  TicketStatus,
  TicketSummary,
  TicketType,
} from './dto.ts';

export type {
  AccountDetail,
  AccountPackage,
  AccountState,
  NewOwner,
  AccountUsage,
  Allowance,
  PackageView,
  PlatformAccount,
  PlatformTenant,
  RepricedAccountView,
  SubscriptionView,
  AccountScore,
  FrameworkId,
  PlatformScores,
  ScoreCell,
  ScoreDefinition,
  ScorePosition,
  ScoreSpread,
  TenantScore,
  AlertKind,
  AlertState,
  AlertView,
  Attestation,
  AuditEvent,
  AuditResponse,
  BrandingLimits,
  BrandingSettings,
  BrandingView,
  Capability,
  ChaseTenant,
  ComparabilityBreak,
  ConsentStatus,
  ControlHistory,
  ControlObservation,
  ControlState,
  ControlSummary,
  CsvRowError,
  DeliveryConfig,
  DeliveryUpdate,
  DeliveryView,
  DemoFixture,
  DriftChange,
  DriftReport,
  EstateFindingRow,
  EstateFindingsResponse,
  EvidenceItem,
  ExcessPermissionTenant,
  FailingRun,
  Finding,
  Framework,
  ImportEntry,
  ImportResult,
  ImportedTenant,
  MeView,
  Narrative,
  NewUser,
  OnboardingSummary,
  OnboardingView,
  PermissionRef,
  PortfolioTenant,
  ProductSummary,
  RiskAcceptance,
  ProgressCounts,
  ProgressReport,
  ReconsentTenant,
  Remediation,
  Role,
  RunBound,
  Scan,
  ScanResult,
  ScheduleDecisionView,
  ScheduleOutcome,
  SchedulePlan,
  SchedulerConfig,
  SessionView,
  Severity,
  Tenant,
  TenantAlertCount,
  TenantBand,
  TenantSchedule,
  UserState,
  UserView,
  Verdict,
  WaitlistJoin,
  WaitlistSignup,
} from './dto.ts';

interface MePayload {
  user: {
    id: string | null;
    email: string | null;
    displayName: string;
    role: Role;
    state: UserState;
    createdAt: string | null;
    lastSignedInAt: string | null;
  };
  capabilities: Capability[];
  breakGlass?: boolean;
  platformOwner?: boolean;
  restricted?: string | null;
}


const toMe = (payload: MePayload): MeView => ({
  id: payload.user.id,
  email: payload.user.email,
  displayName: payload.user.displayName,
  role: payload.user.role,
  state: payload.user.state,
  // Defaulted rather than asserted. `can()` is called during render on every
  // screen, and a missing list from an older or newer server must hide controls
  // rather than throw the console away.
  capabilities: payload.capabilities ?? [],
  lastSignedInAt: payload.user.lastSignedInAt,
  breakGlass: payload.breakGlass === true,
  // Absent reads as false, like `breakGlass` above: a server that has not been
  // told about the platform area must hide it rather than offer a link into
  // twelve routes that would all answer 403.
  platformOwner: payload.platformOwner === true,
  // Only the one value this console knows how to draw. An older server sends
  // nothing and a newer one might send a kind this build has no screen for;
  // both get the ordinary console, and the server still refuses what it must.
  restricted: payload.restricted === 'billing' ? 'billing' : null,
});




/**
 * Whether the signed-in person may do a thing.
 *
 * The UI is a convenience: hiding a control the server would refuse saves a
 * pointless round trip and a confusing 403. It is not the control. Every one of
 * these capabilities is enforced again on the server, which is the only place
 * it counts.
 */
export const can = (user: MeView | null, capability: Capability): boolean =>
  user !== null && user.capabilities.includes(capability);

/**
 * Omitting the filter takes the server's default, which excludes resolved
 * alerts. The values are a closed union, so none of them needs encoding.
 */
const stateQuery = (states?: readonly AlertState[]): string =>
  states === undefined || states.length === 0 ? '' : `?state=${states.join(',')}`;

export class ApiError extends Error {
  /**
   * `body` is the decoded JSON payload where the server sent one. Some failures
   * carry structure a caller has to act on — the CSV importer names the rows it
   * could not read — and re-parsing `message` at every call site would lose it.
   *
   * `retryAfter` is the server's `Retry-After` in whole seconds where it sent
   * one. It arrives in a header rather than the body, so it would be lost by
   * the time a screen saw the failure unless it were carried here.
   */
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = undefined,
    readonly retryAfter: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The session is over: it expired, it was revoked, or the account was
 * suspended. Distinct from an ordinary 401 because the shell has to react to it
 * differently — dropping back to sign-in rather than painting an error strip on
 * a screen the person can no longer use.
 */
export class SessionEndedError extends ApiError {
  constructor(message: string, body: unknown = undefined) {
    super(401, message, body);
    this.name = 'SessionEndedError';
  }
}

/** Whether a failure means "no valid session", however it was raised. */
export const isSignedOut = (cause: unknown): boolean =>
  cause instanceof ApiError && cause.status === 401;

const decode = (text: string): unknown => {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
};

/**
 * The sentences inside a Zod `flatten()`, which is what the API sends for a
 * body it refused. The password policy and the email check write theirs to be
 * read as they are ("A password must be at least 12 characters…"), and without
 * this the console said "The server returned 400." in their place.
 */
const validationSentences = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const { formErrors, fieldErrors } = error as { formErrors?: unknown; fieldErrors?: unknown };
  const lists = [
    formErrors,
    ...(typeof fieldErrors === 'object' && fieldErrors !== null ? Object.values(fieldErrors) : []),
  ];
  const sentences = lists
    .flatMap((list) => (Array.isArray(list) ? list : []))
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
  return sentences.length > 0 ? sentences.join(' ') : null;
};

/**
 * A sentence for a person, out of whatever the server sent.
 *
 * Failures render straight into a banner on the screen that caused them, and an
 * `ApiError` carrying the raw response body put `{"error":"entra_not_configured",
 * "detail":"…"}` in front of the operator. The structure still matters to
 * callers — the CSV importer reads the rows it could not parse out of `body` —
 * so this only chooses what to *say*, and leaves `body` alone.
 */
const readable = (status: number, text: string, body: unknown): string => {
  if (typeof body === 'object' && body !== null) {
    const shape = body as { detail?: unknown; error?: unknown };
    if (typeof shape.detail === 'string' && shape.detail.trim()) return shape.detail;
    const refused = validationSentences(shape.error);
    if (refused !== null) return refused;
    // A bare `error` is a machine code like `entra_not_configured`. Better than
    // JSON, still not a sentence, so it is a last resort before the status.
    if (typeof shape.error === 'string' && shape.error.trim()) return shape.error;
  }
  const plain = text.trim();
  if (plain && !plain.startsWith('{') && !plain.startsWith('[')) return plain;
  return `The server returned ${status}.`;
};

const isRowError = (value: unknown): value is CsvRowError =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as CsvRowError).row === 'number' &&
  typeof (value as CsvRowError).reason === 'string';

/**
 * Narrows a rejected CSV import to its per-row reasons, or null if this failure
 * was something else. The shape is checked rather than asserted because the
 * console cannot assume the server it is talking to is the version it expects.
 */
export const csvRowErrors = (cause: unknown): CsvRowError[] | null => {
  if (!(cause instanceof ApiError) || cause.status !== 400) return null;

  const body = cause.body;
  if (typeof body !== 'object' || body === null) return null;

  const detail = (body as { error?: unknown }).error;
  if (typeof detail !== 'object' || detail === null) return null;

  const rows = (detail as { rows?: unknown }).rows;
  if (!Array.isArray(rows)) return null;

  const parsed = rows.filter(isRowError);
  return parsed.length > 0 ? parsed : null;
};

/**
 * `Retry-After`, in whole seconds.
 *
 * The header is either a number of seconds or an HTTP date, and which one
 * arrives depends on what is in front of the API as much as on the API. Both
 * are read here rather than at the one screen that cares.
 */
const retryAfterSeconds = (header: string | null): number | null => {
  if (header === null) return null;

  const seconds = Number(header.trim());
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds);

  const at = Date.parse(header);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.round((at - Date.now()) / 1000));
};

type SessionListener = () => void;

const sessionListeners = new Set<SessionListener>();

/**
 * Every screen already catches its own failures and renders them in place. A
 * session that has ended is the one failure that cannot be handled that way:
 * the screen underneath the error strip is no longer usable, and every
 * subsequent call on it will fail the same way. So it is broadcast, and the
 * shell — the one component that can change what is on screen — listens.
 */
const sessionEnded = (): void => {
  for (const listener of sessionListeners) listener();
};

/**
 * @param sessionScoped
 *   Whether a 401 from this call means the session ended. It does for every
 *   call made from inside the console. It does not for the three that are
 *   *about* a credential — signing in, asking who we are before we know, and
 *   changing a password — where a 401 is the answer rather than an expiry, and
 *   dropping the whole shell would be wrong.
 */
/** The audit screen's filters. Every one is optional and additive. */
export interface AuditLogQuery {
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
  /** The numeric id of the last row already shown: the keyset cursor. */
  before?: string;
  limit?: number;
}

const auditQueryString = (query?: AuditLogQuery): string => {
  const params = new URLSearchParams();
  if (query?.action) params.set('action', query.action);
  if (query?.actor) params.set('actor', query.actor);
  if (query?.from) params.set('from', query.from);
  if (query?.to) params.set('to', query.to);
  if (query?.before) params.set('before', query.before);
  if (query?.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/*
 * Exported for the modules that carry a group of calls of their own
 * (`billingApi.ts`) so that this file stops growing past its ceiling. They
 * send through this one function, so every rule above — the cookie, the
 * content-type, the session-ended broadcast — holds for them without a copy.
 */
export const request = async <T>(
  path: string,
  init: RequestInit = {},
  sessionScoped = true,
): Promise<T> => {
  const response = await fetch(`/api${path}`, {
    ...init,
    // The session is an httpOnly cookie. Script cannot read it — which is the
    // point, because localStorage is readable by anything that gets injected
    // and this console holds many organisations' security posture — so this is
    // the line that makes the browser attach it.
    credentials: 'same-origin',
    headers: {
      // Only declare a JSON body when one is actually being sent. Fastify
      // refuses an empty body that arrives with `content-type:
      // application/json` — "Body cannot be empty" — and roughly a dozen of
      // this console's mutations carry no body at all: running a scan,
      // archiving a client, signing out, sweeping. Sending the header
      // unconditionally 400'd every one of them, and did it invisibly until a
      // tenant existed to run a scan against.
      ...(init.body === undefined || init.body === null
        ? {}
        : { 'content-type': 'application/json' }),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    const text = await response.text();
    const body = decode(text);
    const message = readable(response.status, text, body);

    if (response.status === 401 && sessionScoped) {
      sessionEnded();
      throw new SessionEndedError(message, body);
    }
    if (response.status === 403 && isAccountSuspended(body)) accountSuspended();

    throw new ApiError(
      response.status,
      message,
      body,
      retryAfterSeconds(response.headers.get('retry-after')),
    );
  }
  return (await response.json()) as T;
};

/**
 * Open a rendered document at its own address in a new tab (`openDocument.ts`,
 * ADR-0080), with a refusal worded the way `request` words one: a report that
 * will not open should say why in a sentence, not hand the operator JSON.
 */
const openHtml = (path: string, options: OpenReportOptions = {}): Promise<void> =>
  openDocument(path, options, (status, text) => {
    const body = decode(text);
    return new ApiError(status, readable(status, text, body), body);
  });

export const api = {
  /**
   * Told when a call fails because the session is over. Returns the
   * unsubscribe, so an effect can hand it straight back to React.
   */
  onSessionEnded: (listener: () => void): (() => void) => {
    sessionListeners.add(listener);
    return () => {
      sessionListeners.delete(listener);
    };
  },

  /**
   * Sign in. A 401 here is the answer to the question, not an expiry, so it is
   * deliberately not session-scoped: it must not knock the shell over.
   *
   * The server returns the same body for an unknown address, a wrong password
   * and a suspended account. The console keeps that promise by never looking at
   * which one it was.
   */
  signIn: (email: string, password: string) =>
    request<MePayload>(
      '/auth/sign-in',
      { method: 'POST', body: JSON.stringify({ email, password }) },
      false,
    ).then(toMe),

  signOut: () => request<void>('/auth/sign-out', { method: 'POST' }, false),

  /**
   * Who is signed in, if anyone. Not session-scoped: this is the call that
   * *asks* whether there is a session, so a 401 is a normal answer and
   * broadcasting it would announce the end of a session that never began.
   */
  me: () => request<MePayload>('/auth/me', {}, false).then(toMe),

  /**
   * Join the waiting list from the public landing page.
   *
   * Not session-scoped, and the only call in this file made by somebody who is
   * not signed in. There is no session to end, so a 401 here would be a
   * misconfiguration rather than an expiry, and broadcasting one would drop a
   * stranger onto a sign-in screen they have no business seeing.
   *
   * The server answers identically whether the address is new or already on the
   * list. The console keeps that promise the same way `signIn` keeps its own:
   * by never looking at which it was.
   */
  joinWaitlist: (join: WaitlistJoin) =>
    request<{ joined: boolean }>(
      '/waitlist',
      { method: 'POST', body: JSON.stringify(join) },
      false,
    ),

  /** The waiting list, newest first. Owner-only on the server. */
  listRiskAcceptances: (tenantId: string) =>
    request<{ acceptances: RiskAcceptance[] }>(`/tenants/${tenantId}/risk-acceptances`),

  /**
   * The server decides what is being accepted — the severity and a digest of
   * the finding — from the client's most recent assessment. The caller supplies
   * only the client's own decision.
   */
  acceptRisk: (
    tenantId: string,
    controlId: string,
    body: { acceptedBy: string; reason: string; reviewBy: string },
  ) =>
    request<{ acceptance: RiskAcceptance }>(`/tenants/${tenantId}/risk-acceptances/${controlId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  withdrawRiskAcceptance: (tenantId: string, controlId: string) =>
    request<void>(`/tenants/${tenantId}/risk-acceptances/${controlId}`, { method: 'DELETE' }),

  listWaitlist: () => request<{ signups: WaitlistSignup[] }>('/platform/waitlist'),

  listSessions: () => request<{ sessions: SessionView[] }>('/auth/sessions'),

  revokeSession: (sessionId: string) =>
    request<void>(`/auth/sessions/${sessionId}/revoke`, { method: 'POST' }),

  /**
   * Change your own password. Not session-scoped, because the server answers a
   * wrong current password with a 401 as well: treating that as an expiry would
   * throw somebody out of the console for a typo.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ revokedSessions: number }>(
      '/auth/password',
      { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) },
      false,
    ),

  /**
   * Managing accounts. Every one of these needs `users.manage`, and the server
   * says so with a 403 whatever this console chooses to show.
   *
   * The mutations declare no response body and the screen reloads the list
   * after each. The contract does not promise what they return, and a screen
   * that patched a row from an unpromised payload would be reading a shape the
   * server has not agreed to send.
   */
  listUsers: () => request<{ users: UserView[] }>('/users'),

  createUser: (user: NewUser) =>
    request<void>('/users', { method: 'POST', body: JSON.stringify(user) }),

  setUserRole: (userId: string, role: Role) =>
    request<void>(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),

  suspendUser: (userId: string) => request<void>(`/users/${userId}/suspend`, { method: 'POST' }),

  reinstateUser: (userId: string) => request<void>(`/users/${userId}/reinstate`, { method: 'POST' }),

  setUserPassword: (userId: string, password: string) =>
    request<void>(`/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  revokeUserSessions: (userId: string) =>
    request<{ revokedSessions: number }>(`/users/${userId}/sessions`, { method: 'DELETE' }),

  /**
   * The live estate by default. `includeArchived` asks for the whole estate,
   * archived rows included, which only the Admin screen wants — the archived
   * rows carry `archivedAt` so it can tell them apart.
   */
  listTenants: (options?: { includeArchived?: boolean }) =>
    request<{ tenants: Tenant[] }>(
      options?.includeArchived === true ? '/tenants?includeArchived=true' : '/tenants',
    ),

  createTenant: (entraTenantId: string, displayName: string) =>
    request<{ tenant: Tenant; consentUrl: string }>('/tenants', {
      method: 'POST',
      body: JSON.stringify({ entraTenantId, displayName }),
    }),

  /**
   * Edit a tenant's human details. An omitted field is left unchanged; an
   * explicit `null` on a contact field clears it — the server distinguishes the
   * two, which is what lets one form both set and remove a contact.
   */
  editTenant: (
    id: string,
    details: { displayName?: string; contactEmail?: string | null; contactName?: string | null },
  ) =>
    request<{ tenant: Tenant }>(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(details),
    }),

  /** Archive a tenant: a reversible soft delete. Owner-only on the server. */
  archiveTenant: (id: string) =>
    request<{ tenant: Tenant }>(`/tenants/${id}/archive`, { method: 'POST' }),

  unarchiveTenant: (id: string) =>
    request<{ tenant: Tenant }>(`/tenants/${id}/unarchive`, { method: 'POST' }),

  /**
   * The hard purge. Refused with 409 `{ error: 'not_archived', detail }` unless
   * the tenant is already archived, so the irreversible step is always preceded
   * by the reversible one.
   */
  purgeTenant: (id: string) =>
    request<{ purged: boolean; tenantId: string }>(`/tenants/${id}`, { method: 'DELETE' }),

  consentUrl: (tenantId: string) =>
    request<{ consentUrl: string }>(`/tenants/${tenantId}/consent-url`),

  importTenants: (entries: ImportEntry[], batch?: string) =>
    request<ImportResult>('/tenants/import', {
      method: 'POST',
      body: JSON.stringify({ entries, batch }),
    }),

  importTenantsCsv: (csv: string) =>
    request<ImportResult>('/tenants/import/csv', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }),

  onboarding: () => request<OnboardingView>('/tenants/onboarding'),

  markInvited: (tenantId: string) =>
    request<void>(`/tenants/${tenantId}/invited`, { method: 'POST' }),

  remind: (tenantId: string) =>
    request<{ reminderCount: number }>(`/tenants/${tenantId}/remind`, { method: 'POST' }),

  putOnboardingNote: (tenantId: string, note: string | null) =>
    request<void>(`/tenants/${tenantId}/onboarding-note`, {
      method: 'PUT',
      body: JSON.stringify({ note }),
    }),

  listScans: (tenantId: string) => request<{ scans: Scan[] }>(`/tenants/${tenantId}/scans`),

  startScan: (tenantId: string) =>
    request<{ queued: boolean }>(`/tenants/${tenantId}/scans`, { method: 'POST' }),

  listAttestations: (tenantId: string) =>
    request<{ attestations: Attestation[]; keys: string[] }>(`/tenants/${tenantId}/attestations`),

  putAttestation: (tenantId: string, body: Omit<Attestation, 'attestedAt'>) =>
    request<void>(`/tenants/${tenantId}/attestations`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  listOverrides: (tenantId: string) =>
    request<{ overrides: Record<string, Record<string, unknown>> }>(
      `/tenants/${tenantId}/control-overrides`,
    ),

  putOverride: (tenantId: string, controlId: string, parameters: Record<string, unknown>, reason: string | null) =>
    request<void>(`/tenants/${tenantId}/control-overrides/${controlId}`, {
      method: 'PUT',
      body: JSON.stringify({ parameters, reason }),
    }),

  listControls: () =>
    request<{ products: ProductSummary[]; controls: ControlSummary[] }>('/meta/controls'),

  listFrameworks: () => request<{ frameworks: Framework[] }>('/meta/frameworks'),

  portfolio: () => request<{ tenants: PortfolioTenant[] }>('/portfolio'),

  scanFindings: (tenantId: string, scanId: string) =>
    request<{ findings: Finding[] }>(`/tenants/${tenantId}/scans/${scanId}/findings`),

  scanDrift: (tenantId: string, scanId: string) =>
    request<{ drift: DriftReport | null; detail?: string }>(
      `/tenants/${tenantId}/scans/${scanId}/drift`,
    ),

  /**
   * How this client has moved across its recent assessments.
   *
   * Not keyed on a scan: the answer spans the window rather than one run, and
   * the server bounds the window itself. The reasoning is entirely the server's
   * — this console renders what it is given and computes nothing over it.
   */
  progress: (tenantId: string) =>
    request<{ progress: ProgressReport }>(`/tenants/${tenantId}/progress`),

  /**
   * Branding is deployment-level (ADR-0020), so there is no tenant in any of
   * these paths. The GET never 404s: a deployment that has never set branding
   * gets an all-null settings object and a null `updatedAt`.
   */
  getBranding: () => request<BrandingView>('/branding'),

  putBranding: (branding: BrandingSettings) =>
    request<{ branding: BrandingSettings; updatedAt: string }>('/branding', {
      method: 'PUT',
      body: JSON.stringify(branding),
    }),

  clearBranding: () => request<void>('/branding', { method: 'DELETE' }),

  /**
   * What the next sweep would do. Reading it queues nothing — the route is
   * read-only by design, so fetching on mount and refreshing are both free.
   */
  getSchedule: () => request<SchedulePlan>('/schedule'),

  putTenantSchedule: (tenantId: string, schedule: TenantSchedule) =>
    request<void>(`/tenants/${tenantId}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(schedule),
    }),

  /**
   * Which frameworks this client's reports are projected against. `null` puts
   * them back on all of them, and is not the same request as sending every id:
   * one follows the catalogue, the other pins today's list.
   */
  /*
   * The platform area. Every one of these is refused with a 403 to anybody who
   * is not DriftWatch's own staff, so the console hides the section rather than
   * offering links into twelve routes that would all say no.
   */
  /**
   * The signed-in account's own package. Not a platform call — every operator
   * may read it, because a limit nobody can see is one they meet as a refusal
   * halfway through onboarding a client.
   */
  accountPackage: () => request<AccountPackage>('/account/package'),

  platformAccounts: () => request<{ accounts: PlatformAccount[] }>('/platform/accounts'),

  platformAccount: (id: string) => request<AccountDetail>(`/platform/accounts/${id}`),

  createAccount: (body: { name: string; billingEmail: string | null }) =>
    request<{ account: PlatformAccount }>('/platform/accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateAccount: (id: string, body: { name: string; billingEmail: string | null }) =>
    request<{ account: PlatformAccount }>(`/platform/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  setAccountState: (id: string, body: { state: string; detail: string | null }) =>
    request<{ account: PlatformAccount }>(`/platform/accounts/${id}/state`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Give a client its first — or another — owner. The password is posted once
   * and never returned; the screen shows the copy it already holds, once.
   */
  createPlatformOwner: (accountId: string, owner: NewOwner) =>
    request<{ user: UserView }>(`/platform/accounts/${accountId}/owners`, {
      method: 'POST',
      body: JSON.stringify(owner),
    }),

  /** Replace a client operator's password and end every session they hold. */
  resetOperatorPassword: (accountId: string, userId: string, password: string) =>
    request<{ revokedSessions: number }>(
      `/platform/accounts/${accountId}/users/${userId}/password`,
      { method: 'POST', body: JSON.stringify({ password }) },
    ),

  platformPackages: () =>
    request<{ packages: PackageView[]; frameworks: string[] }>('/platform/packages'),

  createPackage: (body: Allowance & { name: string; description: string | null }) =>
    request<{ package: PackageView }>('/platform/packages', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Saving an edit reprices every client on the package. `note` is the sentence
   * that says so, naming any client the new numbers have just stopped, and the
   * screen renders it verbatim — see `Packages.tsx`.
   */
  updatePackage: (id: string, body: Allowance & { name: string; description: string | null }) =>
    request<{ package: PackageView; applied: RepricedAccountView[]; note: string }>(`/platform/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  retirePackage: (id: string, retired: boolean) =>
    request<{ package: PackageView }>(`/platform/packages/${id}/retire`, {
      method: 'POST',
      body: JSON.stringify({ retired }),
    }),

  assignSubscription: (accountId: string, body: { packageId: string; note: string | null }) =>
    request<{ subscription: SubscriptionView }>(`/platform/accounts/${accountId}/subscription`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  amendSubscription: (accountId: string, body: Allowance & { note: string | null }) =>
    request<{ subscription: SubscriptionView }>(`/platform/accounts/${accountId}/subscription`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  platformTenants: () => request<{ tenants: PlatformTenant[] }>('/platform/tenants'),

  /**
   * Severity-weighted scores for every tenant and account, and the platform's
   * spread (ADR-0081). Staff only, and not merely because the server says so:
   * no screen calls this unless `me.platformOwner` is true, so a customer's
   * browser never even asks — a 403 in their network tab would still tell them
   * the thing exists and what it is called.
   */
  platformScores: () => request<PlatformScores>('/platform/scores'),

  putTenantFrameworks: (tenantId: string, frameworks: string[] | null) =>
    request<void>(`/tenants/${tenantId}/frameworks`, {
      method: 'PUT',
      body: JSON.stringify({ frameworks }),
    }),

  /**
   * Run the sweep now, on demand. Unlike `getSchedule`, this enqueues scans. It
   * reports what it did in the same shape the preview uses. Owner-only on the
   * server, so the console offers it only behind `deployment.manage`.
   */
  runSweep: () => request<SchedulePlan>('/schedule/run', { method: 'POST' }),

  getSchedulerConfig: () => request<SchedulerConfig>('/schedule/config'),

  putSchedulerConfig: (config: { defaultCadenceDays: number | null; maxPerTick: number | null }) =>
    request<SchedulerConfig>('/schedule/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  /** Every control failing anywhere in the live estate, worst severity first. */
  estateFindings: () => request<EstateFindingsResponse>('/estate/findings'),

  /**
   * The audit trail, newest first. Every filter is optional and additive;
   * `before` is the numeric id of the last row already shown, the keyset cursor
   * the server pages on.
   */
  auditLog: (query?: AuditLogQuery) => request<AuditResponse>(`/audit${auditQueryString(query)}`),

  /**
   * The whole deployment's trail, for DriftWatch's own staff. The same query and
   * the same rows as `auditLog`; only the audience differs, and the server's
   * `platform` requirement is what enforces it.
   */
  platformAuditLog: (query?: AuditLogQuery) =>
    request<AuditResponse>(`/platform/audit${auditQueryString(query)}`),

  /**
   * Delivery is deployment-level, like branding, so there is no tenant in
   * either path. The GET never 404s: a deployment that has never configured
   * delivery gets the off state and a null `updatedAt`.
   */
  getDelivery: () => request<DeliveryView>('/delivery'),

  putDelivery: (update: DeliveryUpdate) =>
    request<DeliveryView>('/delivery', { method: 'PUT', body: JSON.stringify(update) }),

  /** Every live alert across the estate, with the per-tenant tallies. */
  listAlerts: (states?: readonly AlertState[]) =>
    request<{ alerts: AlertView[]; counts: TenantAlertCount[] }>(`/alerts${stateQuery(states)}`),

  listTenantAlerts: (tenantId: string, states?: readonly AlertState[]) =>
    request<{ alerts: AlertView[] }>(`/tenants/${tenantId}/alerts${stateQuery(states)}`),

  /**
   * Who acknowledged an alert is a name the operator types rather than the
   * identity the server already has from the session. That predates operator
   * accounts and has outlived the reason for it; until it is reconsidered, the
   * field is what the trail records. The server stores it against the alert and
   * returns the alert it stored, which is what the console reconciles its
   * optimistic row against.
   */
  acknowledgeAlert: (tenantId: string, alertId: string, acknowledgedBy: string) =>
    request<{ alert: AlertView }>(`/tenants/${tenantId}/alerts/${alertId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ acknowledgedBy }),
    }),

  // --- demo: the pipeline with no tenant, for review before an app
  // registration exists. These write nothing.
  /*
   * Support. The console's calls are the signed-in account's own tickets; the
   * server scopes them to that account, so nothing here names one.
   */
  supportTickets: () => request<{ tickets: TicketSummary[] }>('/support/tickets'),

  supportTicket: (id: string) =>
    request<{ ticket: TicketDetail }>(`/support/tickets/${encodeURIComponent(id)}`),

  openSupportTicket: (ticket: NewTicket) =>
    request<{ ticket: TicketDetail }>('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(ticket),
    }),

  replyToSupportTicket: (id: string, message: NewTicketMessage) =>
    request<{ ticket: TicketDetail }>(`/support/tickets/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    }),

  /**
   * An address rather than a call: the image is shown by the browser, which
   * sends the session cookie itself. One route for both the console and the
   * platform area — the server decides who may read it.
   */
  supportAttachmentUrl: (id: string) => `/api/support/attachments/${encodeURIComponent(id)}`,

  platformSupportTickets: (status?: TicketStatus) =>
    request<{ tickets: PlatformTicketSummary[] }>(
      `/platform/support/tickets${status === undefined ? '' : `?status=${status}`}`,
    ),

  platformSupportTicket: (id: string) =>
    request<{ ticket: PlatformTicketDetail }>(
      `/platform/support/tickets/${encodeURIComponent(id)}`,
    ),

  platformReplyToSupportTicket: (id: string, message: NewTicketMessage) =>
    request<{ ticket: PlatformTicketDetail }>(
      `/platform/support/tickets/${encodeURIComponent(id)}/messages`,
      { method: 'POST', body: JSON.stringify(message) },
    ),

  setSupportTicketStatus: (id: string, status: 'closed' | 'awaiting_support') =>
    request<{ ticket: PlatformTicketDetail }>(
      `/platform/support/tickets/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: JSON.stringify({ status }) },
    ),

  demoFixtures: () => request<{ fixtures: DemoFixture[] }>('/demo/fixtures'),

  demoScan: (fixture: string) => request<{ result: ScanResult }>(`/demo/scan/${fixture}`),

  demoDrift: (from: string, to: string) =>
    request<{ drift: DriftReport }>(`/demo/drift/${from}/${to}`),

  demoReportPath: (fixture: string) => `/demo/report/${fixture}`,

  demoDriftReportPath: (from: string, to: string) => `/demo/drift-report/${from}/${to}`,

  /** Open a rendered demo report in a new tab, optionally printing it. */
  openRendered: (path: string, options?: OpenReportOptions): Promise<void> =>
    openHtml(path, options),

  /** Open a stored report in a new tab, optionally printing it. */
  openReport: (tenantId: string, scanId: string, options?: OpenReportOptions): Promise<void> =>
    openHtml(`/tenants/${tenantId}/scans/${scanId}/report`, options),

  /**
   * Open a service review for a stated period, optionally printing it.
   *
   * The same mechanism as every other rendered document this console opens —
   * one probe, one tab, one optional print — rather than a second one for a
   * second kind of document. The dates go out as the operator typed them and
   * the server decides what period they mean: a console that normalised them
   * first would be a second implementation of the boundary rule, and the two
   * would part company at the edges of a quarter.
   */
  openServiceReview: (
    tenantId: string,
    from: string,
    to: string,
    options?: OpenReportOptions,
  ): Promise<void> =>
    openHtml(
      `/tenants/${tenantId}/service-review?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      options,
    ),
};
